import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Settings } from "lucide-react";
import { cx } from "../../../pages/admin/_shared";
import { useAdminNotificationsContext } from "./AdminNotificationsContext";
import { useNotificationFeed } from "./useNotificationFeed";
import NotificationList, { MarkAllReadButton } from "./NotificationList";

/** Chuông thông báo trên header: badge số chưa đọc + dropdown danh sách. */
export default function NotificationBell() {
  const { unread } = useAdminNotificationsContext();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const rootRef = useRef(null);
  const feed = useNotificationFeed({ filter: tab, pageSize: 12, enabled: open });

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const tabCls = (on) =>
    cx(
      "relative -mb-px inline-flex items-center gap-1.5 border-b-2 px-1 pb-2.5 pt-1 text-[13px] font-medium transition",
      on ? "border-admin-primary text-admin-text" : "border-transparent text-admin-textMuted hover:text-admin-text"
    );

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={unread > 0 ? `Thông báo, ${unread} chưa đọc` : "Thông báo"}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={cx(
          "relative inline-flex h-9 w-9 items-center justify-center rounded-lg transition",
          open ? "bg-admin-muted text-admin-text" : "text-admin-textMuted hover:bg-admin-muted hover:text-admin-text"
        )}
      >
        <Bell size={18} className={unread > 0 ? "origin-top [animation:admin-bell-ring_1.2s_ease-in-out_1]" : ""} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-admin-danger px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-admin-surface">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Thông báo"
          className="fixed inset-x-3 top-16 z-50 overflow-hidden rounded-xl border border-admin-border bg-admin-surface shadow-soft-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[420px]"
          style={{ animation: "admin-pop-in 0.15s ease-out" }}
        >
          <div className="flex items-center justify-between gap-2 px-4 pt-3">
            <p className="text-[15px] font-semibold text-admin-text">Thông báo</p>
            <div className="flex items-center gap-1">
              <MarkAllReadButton feed={feed} unread={unread} />
              <Link
                to="/admin/notifications?tab=settings"
                onClick={() => setOpen(false)}
                title="Cài đặt thông báo"
                aria-label="Cài đặt thông báo"
                className="flex h-7 w-7 items-center justify-center rounded-md text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text"
              >
                <Settings size={15} />
              </Link>
            </div>
          </div>

          <div role="tablist" className="mt-2 flex items-center gap-4 border-b border-admin-border px-4">
            <button type="button" role="tab" aria-selected={tab === "all"} onClick={() => setTab("all")} className={tabCls(tab === "all")}>
              Tất cả
            </button>
            <button type="button" role="tab" aria-selected={tab === "unread"} onClick={() => setTab("unread")} className={tabCls(tab === "unread")}>
              Chưa đọc
              {unread > 0 ? <span className="rounded-full bg-admin-primaryLight px-1.5 text-[10px] font-semibold leading-4 text-admin-primary">{unread}</span> : null}
            </button>
          </div>

          <div className="max-h-[min(60vh,460px)] overflow-y-auto">
            <NotificationList
              feed={feed}
              compact
              onNavigate={() => setOpen(false)}
              emptyHint={tab === "unread" ? "Bạn đã đọc hết thông báo." : undefined}
            />
          </div>

          <div className="border-t border-admin-border bg-admin-muted/40 px-4 py-2.5 text-center">
            <Link to="/admin/notifications" onClick={() => setOpen(false)} className="text-xs font-semibold text-admin-primary hover:underline">
              Xem tất cả thông báo
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
